import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { formatDisplayName } from '@/lib/formatName';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, CheckCircle2, Clock } from 'lucide-react';
import { useClinicStaff } from '@/hooks/useClinicStaff';
import { useAcknowledgePolicy } from '@/hooks/useAcknowledgePolicy';
import { useAuth } from '@/contexts/AuthContext';
import type { PolicyAcknowledgement, PolicyVersion } from '@/hooks/usePolicy';

interface AcknowledgementsTabProps {
  policyId: string;
  acknowledgements: PolicyAcknowledgement[];
  latestVersion: PolicyVersion | null;
  versions: PolicyVersion[];
  isLoading: boolean;
}

function getVersionLabel(version: PolicyVersion, latestVersion: PolicyVersion | null): string {
  const num = `v${version.version_number}`;
  if (version.id === latestVersion?.id && version.version_status === 'approved') {
    return `${num} — Approved`;
  }
  if (version.version_status === 'approved') {
    return `${num} — Superseded`;
  }
  const status = (version.version_status ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
  return `${num} — ${status}`;
}

export function AcknowledgementsTab({ 
  policyId,
  acknowledgements, 
  latestVersion,
  versions,
  isLoading 
}: AcknowledgementsTabProps) {
  const { profile } = useAuth();
  const { data: staffMembers = [], isLoading: staffLoading } = useClinicStaff();
  const acknowledgeMutation = useAcknowledgePolicy(policyId);

  const [selectedVersionId, setSelectedVersionId] = useState<string | undefined>(latestVersion?.id);

  // Sync default when latestVersion loads
  useEffect(() => {
    if (latestVersion && !selectedVersionId) {
      setSelectedVersionId(latestVersion.id);
    }
  }, [latestVersion, selectedVersionId]);

  const selectedVersion = versions.find(v => v.id === selectedVersionId) ?? latestVersion;

  // Get acknowledgements for the selected version
  const selectedVersionAcks = selectedVersion
    ? acknowledgements.filter(ack => ack.policy_version_id === selectedVersion.id)
    : [];

  // Build status map for each staff member (for selected version)
  const staffStatusMap = new Map<string, PolicyAcknowledgement | null>();
  staffMembers.forEach(staff => {
    const ack = selectedVersionAcks.find(a => a.acknowledged_by === staff.id);
    staffStatusMap.set(staff.id, ack ?? null);
  });

  // Check if current user has acknowledged the latest version (always latest, not selected)
  const latestVersionAcks = latestVersion
    ? acknowledgements.filter(ack => ack.policy_version_id === latestVersion.id)
    : [];
  const currentUserAck = profile?.id 
    ? latestVersionAcks.find(ack => ack.acknowledged_by === profile.id)
    : null;

  // Any user can acknowledge (including admins) unless they already have
  const canAcknowledge = latestVersion && !currentUserAck;

  const handleAcknowledge = () => {
    if (latestVersion) {
      acknowledgeMutation.mutate(latestVersion.id);
    }
  };

  if (isLoading || staffLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!latestVersion) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
        <p className="text-muted-foreground">No policy version available.</p>
        <p className="text-sm text-muted-foreground/70">
          Upload a policy version first to enable acknowledgements.
        </p>
      </div>
    );
  }

  const hasAcksForSelectedVersion = selectedVersionAcks.length > 0;

  return (
    <div className="space-y-6">
      {/* Acknowledge button for current user */}
      {canAcknowledge && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium">Acknowledge Policy v{latestVersion.version_number}</p>
              <p className="text-sm text-muted-foreground">
                Please review and acknowledge the latest version of this policy.
              </p>
            </div>
            <Button 
              onClick={handleAcknowledge}
              disabled={acknowledgeMutation.isPending}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {acknowledgeMutation.isPending ? 'Acknowledging...' : 'Acknowledge'}
            </Button>
          </div>
        </div>
      )}

      {/* Already acknowledged message */}
      {currentUserAck && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">You have acknowledged this policy</p>
              <p className="text-sm text-muted-foreground">
                Acknowledged on {format(new Date(currentUserAck.acknowledged_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Staff acknowledgement status table */}
      <div>
        <div className="mb-3 flex items-center gap-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            Staff Acknowledgement Status
          </h3>
          {versions.length > 1 ? (
            <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
              <SelectTrigger className="w-[200px] border-[#339999]">
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    {getVersionLabel(v, latestVersion)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm text-muted-foreground">(v{selectedVersion?.version_number ?? latestVersion.version_number})</span>
          )}
        </div>
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader className="bg-[hsl(var(--table-header))]">
              <TableRow>
                <TableHead>Staff Member</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Acknowledged</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!hasAcksForSelectedVersion && staffMembers.length > 0 && selectedVersion?.id !== latestVersion?.id ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No acknowledgements recorded for this version.
                  </TableCell>
                </TableRow>
              ) : staffMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No staff members found.
                  </TableCell>
                </TableRow>
              ) : (
                staffMembers.map((staff) => {
                  const ack = staffStatusMap.get(staff.id);
                  return (
                    <TableRow key={staff.id}>
                      <TableCell className="font-medium">{formatDisplayName(staff.name, staff.credentials)}</TableCell>
                      <TableCell className="text-muted-foreground">{staff.email}</TableCell>
                      <TableCell>
                        {ack ? (
                          <Badge variant="default">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Acknowledged
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Clock className="mr-1 h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {ack 
                          ? format(new Date(ack.acknowledged_at), 'MMM d, yyyy h:mm a')
                          : '—'
                        }
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
